// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { transform } from "../../../core/Paperwork/US_TN/CompliantReporting/Transformer";
import { OpportunityFormComponentName } from "../../../core/WorkflowsLayouts";
import { UsTnCompliantReporting2025PolicyOpportunity } from "../UsTn/UsTnCompliantReporting2025PolicyOpportunity";
import type {
  CompliantReportingDraftData,
  CompliantReportingTransformedETLFormInput,
} from "./CompliantReportingFormTypes";
import { FormBase } from "./FormBase";

export type {
  CompliantReportingDraftData,
  CompliantReportingTransformedETLFormInput,
};

// TN fines and fees data is unavailable during the TOMIS 2.0 cutover, so while
// usTnHideFinesAndFees is on these fields are left blank for staff to fill in.
export const US_TN_CR_BLANK_FEE_FIELDS = {
  supervisionFeeAssessed: "",
  supervisionFeeArrearaged: false,
  supervisionFeeArrearagedAmount: "",
  supervisionFeeWaived: "",
  supervisionFeeExemptionType: "",
  supervisionFeeExemptionExpirDate: "",
  courtCostsPaid: false,
} satisfies Partial<CompliantReportingDraftData>;

export class CompliantReportingForm extends FormBase<
  CompliantReportingDraftData,
  UsTnCompliantReporting2025PolicyOpportunity
> {
  get navigateToFormText(): string {
    return "Auto-fill referral";
  }

  get formContents(): OpportunityFormComponentName {
    return "WorkflowsCompliantReportingForm";
  }

  get downloadText(): string {
    if (this.formIsDownloading) {
      return "Downloading PDF...";
    }

    if (this.opportunity.updates?.completed) {
      return "Re-download PDF";
    }

    return "Download PDF";
  }

  get formType(): string {
    return "CompliantReportingForm";
  }

  prefilledDataTransformer(): Partial<CompliantReportingDraftData> {
    if (!this.opportunity || !this.opportunity.record) return {};
    const prefilled = transform(
      this.person,
      this.opportunity.record,
      this.opportunity,
    );
    if (!this.rootStore.userStore.activeFeatureVariants.usTnHideFinesAndFees) {
      return prefilled;
    }
    return { ...prefilled, ...US_TN_CR_BLANK_FEE_FIELDS };
  }
}
